"use client";

import { useEffect } from "react";
import { OverlayScrollbars } from "overlayscrollbars";

const overlayScrollbarOptions = {
  scrollbars: {
    theme: "os-theme-rati",
    autoHide: "scroll" as const,
    autoHideDelay: 500,
    dragScroll: true,
    clickScroll: true,
    pointers: ["mouse", "touch", "pen"],
  },
};

export function OverlayScrollbarsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  useEffect(() => {
    const instances = new Map<HTMLElement, ReturnType<typeof OverlayScrollbars>>();

    function initTarget(element: HTMLElement) {
      if (instances.has(element)) {
        return;
      }

      const instance = OverlayScrollbars(element, overlayScrollbarOptions);
      instances.set(element, instance);
    }

    function destroyTarget(element: HTMLElement) {
      const instance = instances.get(element);

      if (!instance) {
        return;
      }

      instance.destroy();
      instances.delete(element);
    }

    function initAnnotatedTargets(root: ParentNode) {
      const rootElement = root instanceof HTMLElement ? root : null;
      if (rootElement?.matches("[data-scroll-overlay]")) {
        initTarget(rootElement);
      }

      root.querySelectorAll?.("[data-scroll-overlay]").forEach((element) => {
        initTarget(element as HTMLElement);
      });
    }

    function destroyAnnotatedTargets(root: ParentNode) {
      const rootElement = root instanceof HTMLElement ? root : null;
      if (rootElement?.matches("[data-scroll-overlay]")) {
        destroyTarget(rootElement);
      }

      root.querySelectorAll?.("[data-scroll-overlay]").forEach((element) => {
        destroyTarget(element as HTMLElement);
      });
    }

    initTarget(document.body);
    initAnnotatedTargets(document.body);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            initAnnotatedTargets(node);
          }
        });

        mutation.removedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            destroyAnnotatedTargets(node);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();

      Array.from(instances.keys()).forEach((element) => {
        destroyTarget(element);
      });
    };
  }, []);

  return <>{children}</>;
}
